/**
 * Fetch-and-save downloads with a spinner.  An attachment response does not navigate, so a
 * plain link or submit gives no feedback while the server builds a slow file (a PDF through
 * Chromium, a zip read from storage).  This fetches it, saves the blob under the server's
 * Content-Disposition filename, and keeps the button spinning and disabled until it lands.
 *
 * `fetchDownload(url, options)` is the shared core; `acqs-sow-pdf.js` posts its forms through
 * it.  Options: `init` (fetch init; GET by default), `button` (gets the spinner: its <i> swaps
 * to fa-spinner, or `btn--loading` when it has none), `fallbackName`, `expectType` (a
 * Content-Type the response must contain), `errorMessage`.  Resolves true once the file saved,
 * false on an error (shown as a toast) or when the same URL is already in flight.
 *
 * Markup: `<a href="..." data-download>` (or `data-download="<url>"` on a button) downloads that
 * URL on click this way, e.g. the refund claim sheet's Download PDF and Download Packet.  A
 * modified click (ctrl, cmd, shift, alt, middle) is left to the browser, so the PDF can still open in
 * a tab.  The whole file is held in memory until it lands, which suits files of tens of MB.
 */

// Keyed by URL, not stored on the button: a swap mid-download hands back a fresh, unguarded
// element, and a second click must not start a second request.
var inFlight = new Set();

function filenameFrom(contentDisposition, fallbackName) {
    if (!contentDisposition) return fallbackName;
    // RFC 5987 `filename*=UTF-8''name` (Django's FileResponse for a non-ASCII name) wins.
    var encoded = contentDisposition.match(/filename\*=(?:UTF-8'[^']*')?([^;]+)/i);
    if (encoded) {
        try {
            return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, ''));
        } catch (err) { /* malformed: fall through to the plain filename */ }
    }
    var match = contentDisposition.match(/filename="?([^";]+)"?/i);
    return match ? match[1] : fallbackName;
}

function showError(message) {
    if (window.SeeMe && window.SeeMe.toast) window.SeeMe.toast.error(message);
}

export function fetchDownload(url, options) {
    options = options || {};
    var button = options.button;
    var errorMessage = options.errorMessage || 'Could not download the file. Please try again.';
    if (inFlight.has(url)) return Promise.resolve(false);

    var icon = button && button.querySelector('i');
    var originalIconClass = icon && icon.className;

    function restore() {
        // The button may have been swapped out mid-request; restoring a detached node is
        // harmless, and the replacement renders enabled anyway.
        if (icon) icon.className = originalIconClass;
        if (button) {
            button.disabled = false;
            button.classList.remove('btn--loading');
            button.removeAttribute('aria-disabled');
        }
        inFlight.delete(url);
    }

    // Everything from here runs inside one try: cleanup otherwise exists only once the promise
    // chain does, and a synchronous throw before it (a bad init, fetch throwing) would leave the
    // button disabled and the URL stuck in `inFlight`.
    try {
        inFlight.add(url);
        // A text-only button has no icon to swap, so it takes the overlay spinner, as `loading.js`
        // does for htmx buttons.
        if (icon) icon.className = 'fa-solid fa-spinner fa-spin';
        else if (button) button.classList.add('btn--loading');
        if (button) {
            button.disabled = true;
            button.setAttribute('aria-disabled', 'true');
        }

        var init = Object.assign({credentials: 'same-origin'}, options.init || {});
        return fetch(url, init).then(function (response) {
            if (!response.ok) {
                return response.json().then(function (data) {
                    throw new Error((data && data.error) || errorMessage);
                }, function () {
                    throw new Error(errorMessage);
                });
            }
            var contentType = response.headers.get('Content-Type') || '';
            // A page instead of a file: an expired session lands on the login page with a 200.
            if (contentType.indexOf('text/html') !== -1) {
                throw new Error('Could not download the file.  Reload the page and sign in again if asked.');
            }
            if (options.expectType && contentType.indexOf(options.expectType) === -1) {
                throw new Error(errorMessage);
            }
            return response.blob().then(function (blob) {
                var objectUrl = window.URL.createObjectURL(blob);
                var link = document.createElement('a');
                link.href = objectUrl;
                link.download = filenameFrom(response.headers.get('Content-Disposition'), options.fallbackName || 'download');
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.setTimeout(function () {
                    window.URL.revokeObjectURL(objectUrl);
                }, 1000);
                return true;
            });
        }).catch(function (err) {
            // A network failure rejects with a TypeError ("Failed to fetch"); say ours instead.
            showError(err instanceof TypeError ? errorMessage : (err && err.message) || errorMessage);
            return false;
        }).finally(restore);
    } catch (err) {
        restore();
        showError(errorMessage);
        return Promise.resolve(false);
    }
}

document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-download]');
    if (!trigger) return;
    // A modified click (ctrl, cmd, shift, alt, middle) keeps the browser's own handling (open in a new tab).
    if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    var url = trigger.getAttribute('data-download') || trigger.getAttribute('href');
    if (!url || trigger.getAttribute('aria-disabled') === 'true') return;
    fetchDownload(url, {button: trigger});
});
